import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { buildReportPayload } from "../reports/reportUtils";
import API_URL from "../../config/ApiUrl";

export const getStockSummary = createAsyncThunk(
  "stockReport/getStockSummary",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.STOCK_SUMMARY, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Standalone "Stock Summary" report — its own endpoint/module, separate from the
// Stock Ledger summary (getStockSummary above).
export const getStockSummaryReport = createAsyncThunk(
  "stockReport/getStockSummaryReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.STOCK_SUMMARY_REPORT, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Standalone "Stock Valuation" report — period opening/inward/outward/closing
// stock and value, one row per item. Its own endpoint and module.
export const getStockValuationReport = createAsyncThunk(
  "stockReport/getStockValuationReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.STOCK_VALUATION_REPORT, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getLowStock = createAsyncThunk(
  "stockReport/getLowStock",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.LOW_STOCK, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getStockAging = createAsyncThunk(
  "stockReport/getStockAging",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.STOCK_AGING, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getBinStock = createAsyncThunk(
  "stockReport/getBinStock",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.BIN_STOCK, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getStockAdjustmentReport = createAsyncThunk(
  "stockReport/getStockAdjustmentReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.STOCK_REPORT.STOCK_ADJUSTMENT, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  stockSummary: { list: [], total_records: 0 },
  stockSummaryReport: { list: [], total_records: 0 },
  // `totals` comes from the server and covers the WHOLE filtered set, not the
  // current page — never recompute it by summing `list`.
  stockValuationReport: { list: [], total_records: 0, totals: {} },
  lowStock: { list: [], total_records: 0, summary: {} },
  stockAging: { list: [], total_records: 0, totals: {} },
  binStock: { list: [], total_records: 0 },
  stockAdjustment: { list: [], total_records: 0, totals: {} },
};

const stockReportSlice = createSlice({
  name: "stockReport",
  initialState,
  reducers: {
    resetStockAging: (state) => {
      state.stockAging = { list: [], total_records: 0, totals: {} };
    },
    resetStockAdjustmentReport: (state) => {
      state.stockAdjustment = { list: [], total_records: 0, totals: {} };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStockSummary.fulfilled, (state, action) => {
        state.stockSummary.list = action.payload?.data?.list || [];
        state.stockSummary.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getStockSummaryReport.fulfilled, (state, action) => {
        state.stockSummaryReport.list = action.payload?.data?.list || [];
        state.stockSummaryReport.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getStockValuationReport.fulfilled, (state, action) => {
        state.stockValuationReport.list = action.payload?.data?.list || [];
        state.stockValuationReport.total_records = action.payload?.data?.totalRecords || 0;
        state.stockValuationReport.totals = action.payload?.data?.totals || {};
      })
      .addCase(getLowStock.fulfilled, (state, action) => {
        state.lowStock.list = action.payload?.data?.items || [];
        state.lowStock.total_records = action.payload?.data?.totalRecords || 0;
        state.lowStock.summary = action.payload?.data?.summary || {};
      })
      .addCase(getStockAging.fulfilled, (state, action) => {
        state.stockAging.list = action.payload?.data?.list || [];
        state.stockAging.total_records = action.payload?.data?.totalRecords || 0;
        state.stockAging.totals = action.payload?.data?.totals || {};
      })
      .addCase(getBinStock.fulfilled, (state, action) => {
        state.binStock.list = action.payload?.data?.list || [];
        state.binStock.total_records = action.payload?.data?.totalRecords || 0;
        state.binStock.totals = action.payload?.data?.totals || {};
      })
      .addCase(getStockAdjustmentReport.fulfilled, (state, action) => {
        state.stockAdjustment.list = action.payload?.data?.list || [];
        state.stockAdjustment.total_records = action.payload?.data?.totalRecords || 0;
        state.stockAdjustment.totals = action.payload?.data?.totals || {};
      });
  },
});
export const { resetStockAdjustmentReport, resetStockAging } = stockReportSlice.actions;


export default stockReportSlice.reducer;
